/**
 * The chat header (07 Workshop, Chat header): the chat's title (Shared after
 * it once shared), then who is in it, Share and the chat's ⋯ (Rename, Move to…, Share…, Delete…) and,
 * while the project panel is folded, the button that brings it back. Through
 * the compact shell it is the top lane: a capsule with the menu and the title
 * (it opens the sidebar) and a round button that raises the project panel.
 */
import { Menu, PanelRight, PanelRightOpen, Users } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import type { WorkshopPerson, WorkshopProject } from "@/types/workshop.type";
import { PeopleStack, namesList } from "./PeopleStack";
import WorkshopChatMenu, { type ChatMenuAction } from "./WorkshopChatMenu";

export type RoomSection = "outputs" | "context" | "progress";

type Props = {
  title: string;
  /** Shared chats: everyone in it but the viewer. Null for a private chat. */
  people: WorkshopPerson[] | null;
  /** Shared chats: who has it open now, the viewer excluded. */
  hereNow: WorkshopPerson[];
  /** Opens the chat's people (its owner only). */
  onOpenPeople?: () => void;
  archived: boolean;
  /** The project panel is beside the chat (or up as a sheet). */
  panelOpen: boolean;
  onTogglePanel?: () => void;
  /** Chat actions exist once the chat does. */
  menu: {
    meta: string;
    isOwner: boolean;
    isShared: boolean;
    canEdit: boolean;
    shareDisabled: boolean;
    deleteDisabled: boolean;
    projectId: string;
    projects: WorkshopProject[];
    onAction: (action: ChatMenuAction) => void;
    onMove: (projectId: string) => void;
    onMoveToNewProject: () => void;
  } | null;
  isPhone: boolean;
  /** Compact shell: the capsule stands in for the shell's menu circle. */
  lane?: boolean;
  onOpenSidebar?: () => void;
};

export default function WorkshopThreadHead({
  title,
  people,
  hereNow,
  onOpenPeople,
  archived,
  panelOpen,
  onTogglePanel,
  menu,
  isPhone,
  lane = false,
  onOpenSidebar,
}: Props) {
  const menuNode = menu ? (
    <WorkshopChatMenu
      title={title}
      meta={menu.meta}
      isOwner={menu.isOwner}
      canEdit={menu.canEdit}
      canMove={!menu.isShared}
      shareDisabled={menu.shareDisabled}
      deleteDisabled={menu.deleteDisabled}
      projectId={menu.projectId}
      projects={menu.projects}
      isPhone={isPhone}
      onAction={menu.onAction}
      onMove={menu.onMove}
      onMoveToNewProject={menu.onMoveToNewProject}
      triggerClassName="ws-more ws-head-more"
    />
  ) : null;
  // Shared chats show who has it open (the presence stack); the owner shares
  // it, or opens its people once it is shared, from Share beside ⋯.
  const presence =
    people && people.length > 0 ? (
      <PeopleStack
        people={people.map((p) => ({
          userId: p.userId,
          name: p.name,
          state: hereNow.some((h) => h.userId === p.userId) ? "active" : undefined,
        }))}
        size={26}
        label={`Shared with ${namesList(people, 3)}${
          hereNow.length > 0 ? `. Here now: ${namesList(hereNow, 3)}` : ""
        }`}
      />
    ) : null;
  const shareNode = onOpenPeople ? (
    <KitButton
      variant="secondary"
      iconLeft={Users}
      onClick={onOpenPeople}
      isDisabled={menu?.shareDisabled}
    >
      Share
    </KitButton>
  ) : null;

  if (lane) {
    return (
      <header className="ws-lane">
        <button type="button" className="ws-lane-capsule" onClick={onOpenSidebar} aria-label={`${title}. Open the sidebar`}>
          <Menu aria-hidden="true" />
          <span className="ws-lane-title">{title}</span>
        </button>
        {onTogglePanel ? (
          <button type="button" className="ws-lane-round" onClick={onTogglePanel} aria-label="Project panel">
            <PanelRight aria-hidden="true" />
          </button>
        ) : null}
      </header>
    );
  }

  return (
    <header className="ws-thread-head">
      <div className="ws-thread-lead">
        <h2 className="ws-thread-name" title={title}>
          {title}
        </h2>
        {people ? <span className="ws-thread-shared">Shared</span> : null}
        {archived ? <span className="ws-tag">Archived</span> : null}
      </div>
      <div className="ws-thread-actions">
        {presence}
        {shareNode}
        {menuNode}
        {!panelOpen && onTogglePanel ? (
          <button
            type="button"
            className="ws-more ws-head-more"
            onClick={onTogglePanel}
            aria-label="Show the project panel"
            title="Show the project panel"
            aria-controls="ws-project-room-panel"
            aria-expanded={false}
          >
            <PanelRightOpen aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </header>
  );
}
